import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Mail, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const call = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Request failed");
  return res.json();
};

const STATUS_STYLES = {
  new: "border-gold/60 text-gold",
  contacted: "border-emerald-400/60 text-emerald-300",
  closed: "border-white/25 text-slate-400",
};

const Enquiries = () => {
  const [authed, setAuthed] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const data = await call("/admin/samples");
    setRows(data);
  }, []);

  useEffect(() => {
    call("/admin/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (authed) load().catch(() => setAuthed(false));
  }, [authed, load]);

  const signIn = async (e) => {
    e.preventDefault();
    try {
      await call("/admin/login", { method: "POST", body: JSON.stringify({ passcode }) });
      setPasscode("");
      setAuthed(true);
    } catch {
      toast.error("Invalid passcode");
    }
  };

  const setStatus = async (id, status) => {
    await call(`/admin/samples/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  if (loading) return <div className="min-h-screen bg-navy" />;

  if (!authed) {
    return (
      <div data-testid="enquiries-login" className="grain-overlay flex min-h-screen items-center justify-center bg-navy px-6">
        <form onSubmit={signIn} className="w-full max-w-sm border border-gold/25 bg-white/[0.03] p-8 backdrop-blur-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-champagne">Export Desk</p>
          <h1 className="mt-3 font-serif text-3xl text-white">Enquiry Inbox</h1>
          <p className="mt-2 text-sm text-slate-400">Enter the passcode to view sample requests.</p>
          <input
            data-testid="enquiries-passcode-input"
            type="password"
            autoComplete="current-password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            className="mt-6 w-full border border-white/15 bg-navy/60 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-gold"
          />
          <button
            data-testid="enquiries-login-btn"
            type="submit"
            className="mt-4 w-full bg-gold py-3.5 text-[11px] font-bold uppercase tracking-[0.25em] text-navy transition-colors hover:bg-champagne"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="enquiries-page" className="grain-overlay min-h-screen bg-navy px-5 py-14 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gold/20 pb-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-champagne">Export Desk</p>
            <h1 className="mt-2 font-serif text-4xl text-white">Enquiry Inbox</h1>
            <p className="mt-2 text-sm text-slate-400">
              {rows.length} sample {rows.length === 1 ? "request" : "requests"} received
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="enquiries-refresh-btn"
              onClick={() => load().then(() => toast.success("Refreshed"))}
              className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-200 transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              data-testid="enquiries-logout-btn"
              onClick={async () => {
                await call("/admin/logout", { method: "POST" });
                setAuthed(false);
              }}
              className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-200 transition-colors hover:border-gold hover:text-gold"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <p data-testid="enquiries-empty" className="mt-16 text-center text-sm text-slate-500">
            No sample requests yet.
          </p>
        ) : (
          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {rows.map((r) => (
              <article
                key={r.id}
                data-testid={`enquiry-card-${r.id}`}
                className="border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-gold/40"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif text-2xl text-white">{r.company}</h2>
                    <p className="mt-1 text-sm text-slate-400">{r.contact}</p>
                  </div>
                  <span className={`border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] ${STATUS_STYLES[r.status]}`}>
                    {r.status}
                  </span>
                </div>

                <dl className="mt-5 grid gap-2 text-sm text-slate-300">
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Address</dt><dd>{r.address}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Email</dt><dd>{r.email}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">WhatsApp</dt><dd>{r.whatsapp}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Fabric</dt><dd>{r.fabric || "—"}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Quantity</dt><dd>{r.quantity || "—"}</dd></div>
                  {r.message && (
                    <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Message</dt><dd className="italic text-slate-400">{r.message}</dd></div>
                  )}
                  <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Received</dt><dd>{new Date(r.created_at).toLocaleString()}</dd></div>
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    data-testid={`enquiry-whatsapp-reply-${r.id}`}
                    href={r.reply_whatsapp_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => r.status === "new" && setStatus(r.id, "contacted")}
                    className="flex items-center gap-2 bg-gold px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-champagne"
                  >
                    <MessageCircle size={15} /> Reply on WhatsApp
                  </a>
                  <a
                    data-testid={`enquiry-email-reply-${r.id}`}
                    href={`mailto:${r.email}?subject=${encodeURIComponent(`P.A. International — your sample request (${r.company})`)}`}
                    className="flex items-center gap-2 border border-white/20 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-200 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Mail size={15} /> Email
                  </a>
                  {r.status !== "closed" && (
                    <button
                      data-testid={`enquiry-close-${r.id}`}
                      onClick={() => setStatus(r.id, "closed")}
                      className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-gold"
                    >
                      Mark closed
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Enquiries;
